import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { readApiError } from "_shared/_errors/api-error";
import { HttpStatusCode } from "axios";
import { useGetKnowledgeBaseArticleBySlugQuery } from "knowledge-base/_api/knowledge-base-api";
import ArticleAttachments from "knowledge-base/articles/_views/article-attachments";
import ArticleMarkdown from "knowledge-base/articles/_views/article-markdown";
import { useKnowledgeBaseLocations } from "knowledge-base/knowledge-base-locations";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router";
import { useSelector } from "react-redux";

type LoadFailure = Readonly<{
  severity: "info" | "warning" | "error";
  title: string;
  message: string;
}>;

/** The server's code for an article that exists and that this reader may not open. */
export const ARTICLE_READ_FORBIDDEN_CODE = "knowledge_base_article_read_forbidden";

const NO_ACCESS_TO_ARTICLE: LoadFailure = {
  severity: "warning",
  title: "You do not have access to this page",
  message: "This page exists, but you cannot read it. Ask for access to the knowledge base it is in.",
};

// Every other refusal is decided before the article is looked up - the reader may not read this
// knowledge base at all (knowledge_base_read_forbidden) - so it says nothing about whether the page
// exists.
const NO_ACCESS_TO_KNOWLEDGE_BASE: LoadFailure = {
  severity: "warning",
  title: "You do not have access to this knowledge base",
  message: "You cannot read this knowledge base. Ask an administrator for knowledge base access.",
};

const NO_SUCH_PAGE: LoadFailure = {
  severity: "info",
  title: "No such page",
  message: "This page does not exist. It may have been removed, or the link may be wrong.",
};

const LOAD_FAILED: LoadFailure = {
  severity: "error",
  title: "Could not load this page",
  message: "Something went wrong loading this page. Try again in a moment.",
};

/**
 * Brings the heading a link's fragment names into view: an assistant citation of a section links
 * to its heading (#6019). The router does not scroll to a fragment, and the heading exists only
 * once the article has loaded. A fragment that names no heading - a section renamed since it was
 * cited - leaves the page at its top.
 */
function scrollToHeading(hash: string): void {
  if (!hash) return;
  let id: string;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    return;
  }
  // scrollIntoView is absent in jsdom.
  document.getElementById(id)?.scrollIntoView?.({ block: "start" });
}

function loadFailureFor(error: unknown): LoadFailure {
  const { status, code } = readApiError(error);

  if (status === HttpStatusCode.Forbidden) {
    return code === ARTICLE_READ_FORBIDDEN_CODE ? NO_ACCESS_TO_ARTICLE : NO_ACCESS_TO_KNOWLEDGE_BASE;
  }

  return status === HttpStatusCode.NotFound ? NO_SUCH_PAGE : LOAD_FAILED;
}

type Props = Readonly<{
  slug: string;
  /** Whose knowledge base this article is being read from — see ArticleBySlugArgs. */
  ownerLegalEntityId?: string;
  /**
   * Whether this knowledge base can be written at all, as the server reports it. Distinct from the
   * reader's own permission: a firm may author its own articles and still only read its client's
   * rules, and only an Attorney Hub administrator writes the public help.
   */
  canAuthor?: boolean;
}>;

export default function ArticleReader({ slug, ownerLegalEntityId, canAuthor }: Props) {
  const {
    data: article,
    isLoading,
    isError,
    error,
  } = useGetKnowledgeBaseArticleBySlugQuery({ slug, ownerLegalEntityId });
  const userPermissions = useSelector(getSessionPermissions);
  const { articleEditorPath } = useKnowledgeBaseLocations();
  const { hash } = useLocation();

  useEffect(() => {
    if (article) scrollToHeading(hash);
  }, [article, hash]);

  // An affordance, not an authorization check: the server refuses an edit this reader may not
  // make, whatever the browser shows. Hiding the button for read-only users just avoids offering
  // an action that would fail.
  //
  // Both halves are needed. The permission says whether this person authors knowledge base content
  // for their own organization; canAuthor says whether this particular knowledge base is theirs to
  // write. Without the second, a firm reading its client's rules - and any Attorney Hub member who
  // is not an administrator, reading the public help - was offered an Edit button that the server
  // was always going to refuse.
  const canEdit = Boolean(userPermissions?.includesWriteAccessToKnowledgeBase) && canAuthor === true;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !article) {
    // The server answers 403 for a page that exists and that this reader may not open, and 404
    // only for a page that does not exist. Each gets its own message, because the reader acts on
    // the difference: they ask for access to the first, and stop looking for the second.
    // Only the article's own refusal code confirms that the page exists; see loadFailureFor.
    const failure = loadFailureFor(error);

    return (
      <Alert severity={failure.severity} sx={{ m: 2 }}>
        <AlertTitle>{failure.title}</AlertTitle>
        {failure.message}
      </Alert>
    );
  }

  return (
    <Box component="article" sx={{ maxWidth: 860 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
        <Typography component="h1" data-testid="article-title" variant="h4">
          {article.title}
        </Typography>
        {article.isDraft && <Chip color="warning" label="Draft" size="small" />}
        {article.isArchived && <Chip label="Archived" size="small" variant="outlined" />}
        <Box sx={{ flexGrow: 1 }} />
        {canEdit && (
          <Button
            component={RouterLink}
            size="small"
            startIcon={<EditOutlinedIcon />}
            to={articleEditorPath(article.slug)}
          >
            Edit
          </Button>
        )}
      </Stack>

      {article.isFormLetter && (
        <Alert icon={<EmailOutlinedIcon />} severity="info" sx={{ mb: 3 }}>
          <AlertTitle>This is a letter the application sends</AlertTitle>
          <Typography variant="body2">
            Editing this page changes what recipients read the next time it goes out. The subject
            line is <strong>{article.templateSubject}</strong>.
          </Typography>
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />

      <ArticleMarkdown>{article.body}</ArticleMarkdown>

      {/*
        Read-only here even for an author: this is the page as a reader sees it, and changing what
        hangs off it belongs in the editor. Attachments the reader may not open are still listed,
        as locked rows — see ArticleAttachments.
      */}
      {article.attachedDocumentIds.length > 0 && (
        <>
          <Divider sx={{ mt: 4, mb: 2 }} />
          <ArticleAttachments article={article} />
        </>
      )}

      {article.updated && (
        <>
          <Divider sx={{ mt: 4 }} />
          <Typography color="text.secondary" sx={{ mt: 2 }} variant="caption">
            Last updated {article.updated.toLocaleDateString()}
            {article.currentRevisionNumber ? ` · revision ${article.currentRevisionNumber}` : ""}
          </Typography>
        </>
      )}
    </Box>
  );
}
